import {AfterViewInit, Component, ElementRef, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output} from "@angular/core";
import { SearchContainer } from "src/app/core/definitions/search-container";
import {ModelStore} from "../../core/model-store/model-store";
import {ReportUserGenerated} from "../../core/model-store/models/report-user-generated";
import {Router} from "@angular/router";
import {ReportViewType} from "../../core/model-store/models/report-view-type";
import {AbstractControl, FormGroupDirective, NgForm} from "@angular/forms";
import {ErrorStateMatcher} from "@angular/material/core";

class ShowErrorsAfterSubmit implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control?.invalid && form?.submitted);
  }
}

const OVERLAY_CONTAINER_SELECTOR = '.cdk-overlay-container';
const FORM_CONTROL_SELECTOR = 'input.ng-invalid, textarea.ng-invalid, mat-select.ng-invalid';

const HIDDEN_REPORT_VIEW_TYPE_IDS = [
  'ct_203-33444d86-e4af-497e-a5de-ce1ff9c66e98',
  'ct_203-722c41c1-ff23-4901-af8e-39ab51aaba39',
];

@Component({
    selector: 'new-report-sidemenu',
    templateUrl: './new-report-sidemenu.html',
    styleUrls: ['./new-report-sidemenu.scss'],
    standalone: false,
    providers: [{provide: ErrorStateMatcher, useClass: ShowErrorsAfterSubmit}]
})
export class NewReportSidemenu implements OnInit, AfterViewInit, OnDestroy {
  // Injections
  private readonly modelStore: ModelStore = inject(ModelStore);
  private readonly router: Router = inject(Router);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private returnFocusTarget: HTMLElement | null = null;
  private closing = false;

  @Input() searchContainer!: SearchContainer;
  @Input() newReportObject: any;
  @Output() closed = new EventEmitter<void>();
  newReport: ReportUserGenerated;
  reportViewTypes: ReportViewType[] = [];

  get selectedCount(): number {
    if (this.newReportObject) {
      return 1;
    }
    return this.searchContainer?.selections.selectedItems.length ?? 0;
  }

  get selectableReportViewTypes(): ReportViewType[] {
    return (this.reportViewTypes ?? []).filter(view => !HIDDEN_REPORT_VIEW_TYPE_IDS.includes(view.id));
  }

  async submit(form: NgForm) {
    if (form.invalid) {
      this.focusFirst(FORM_CONTROL_SELECTOR);
      return;
    }
    await this.createReportAndForward();
  }

  async createReportAndForward() {
    this.newReport.artifactList = [];

    if (this.newReportObject?.()) {
      this.newReport.artifactList.push(this.newReportObject());
    } else if (this.searchContainer) {
      this.searchContainer.selections.selectedItems.forEach(item => {
        this.newReport.artifactList.push({ artifact_id: item.artifact_id, superobject_type_id: item.superobject_type_id, object_type: item.object_type });
      });
    }

    this.newReport.reportType = 'custom_user_report';

    let response = this.modelStore.createModel({modelSingularKey: 'report_user_generated',  record: this.newReport});
    await response.loading;
    await this.router.navigate(['/reports/generated-reports', response.id]);
  }

  ngOnInit() {
    this.asyncInit().then()
  }

  ngAfterViewInit() {
    this.returnFocusTarget = document.activeElement as HTMLElement | null;
    this.focusFirst('input[name="title"]');
  }

  ngOnDestroy() {
    this.closing = true;
    if (this.returnFocusTarget?.isConnected) {
      this.returnFocusTarget.focus();
    }
  }

  @HostListener('document:focusin', ['$event'])
  keepFocusInside(event: FocusEvent) {
    const target = event.target as HTMLElement;
    if (this.closing || this.host.nativeElement.contains(target) || target.closest?.(OVERLAY_CONTAINER_SELECTOR)) {
      return;
    }
    this.returnFocusTarget = target;
    this.focusFirst('input[name="title"]');
  }

  async asyncInit() {
    this.newReport = new ReportUserGenerated();
    this.reportViewTypes = this.modelStore.findAllModels('report_view_type');
    // @ts-ignore
    await this.reportViewTypes.loading;
  }

  @HostListener('document:keydown.escape', ['$event'])
  closeOnEscape(event: KeyboardEvent) {
    if (event.defaultPrevented) {
      return;
    }
    this.cancelNewReport();
  }

  cancelNewReport() {
    const currentStep = this.searchContainer?.operationContainer?.currentOperation?.$$currentStep;
    if (currentStep) {
      currentStep.operation_step_type = null;
    }
    this.closed.emit();
  }

  private focusFirst(selector: string) {
    this.host.nativeElement.querySelector<HTMLElement>(selector)?.focus();
  }
}
